import React from "react";
import ReactApexChart from "react-apexcharts";

const MultipleBarGraph = ({
  data,
  categories,
  colors,
  barNames,
  showVerticaldata = false,
  tooltip,
  showDataLabels = false,
  xAxisBorderColor,
  hideHorizontalLines = false,
  height = 430,
}) => {
  const allValues =
    data && Array.isArray(data) ? data.flatMap((s) => s.data || []) : [];
  const realMaxVal = Math.max(...allValues, 0);
  const maxVal = realMaxVal === 0 ? 100 : realMaxVal;
  const minVisualVal = maxVal * 0.01; // 1% of max value for minimum visibility

  let dynamicMax = undefined;
  if (showVerticaldata) {
    if (realMaxVal > 0) {
      if (realMaxVal >= 100) {
        dynamicMax = Math.ceil((realMaxVal * 1.15) / 100) * 100;
      } else if (realMaxVal >= 10) {
        dynamicMax = Math.ceil((realMaxVal * 1.15) / 10) * 10;
      } else {
        dynamicMax = Math.ceil(realMaxVal * 1.15);
      }
    } else {
      dynamicMax = 100;
    }
  }

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    grid: {
      show: true,
      xaxis: { lines: { show: !showVerticaldata } },
      yaxis: {
        lines: { show: hideHorizontalLines ? false : showVerticaldata },
      },
      padding: {
        top: showVerticaldata ? 15 : 0,
      },
    },
    plotOptions: {
      bar: {
        horizontal: !showVerticaldata,
        columnWidth: "60%",
        dataLabels: {
          position: showVerticaldata ? "top" : "right",
          hideOverflowingLabels: false,
        },
      },
    },
    colors,
    dataLabels: {
      enabled: showDataLabels,
      offsetY: showVerticaldata ? -12 : 0,
      offsetX: showVerticaldata ? 0 : 10,
      style: {
        fontSize: "11px",
        fontFamily: "Calibri",
        fontWeight: 700,
        colors: ["#333"],
      },
      formatter: function (val, opts) {
        const seriesIndex = opts?.seriesIndex;
        const dataPointIndex = opts?.dataPointIndex;
        const originalVal =
          data && seriesIndex !== undefined && dataPointIndex !== undefined
            ? data[seriesIndex]?.data[dataPointIndex]
            : undefined;
        return originalVal !== undefined ? originalVal : val;
      },
    },
    legend: { show: false },
    stroke: {
      show: true,
      width: 3,
      colors: ["transparent"],
    },
    tooltip: tooltip || {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val, opts) {
          const seriesIndex = opts?.seriesIndex;
          const dataPointIndex = opts?.dataPointIndex;
          const originalVal =
            data && seriesIndex !== undefined && dataPointIndex !== undefined
              ? data[seriesIndex]?.data[dataPointIndex]
              : undefined;
          return originalVal !== undefined ? originalVal : val;
        },
      },
    },
    xaxis: {
      categories,
      labels: {
        style: {
          fontSize: showVerticaldata ? "12px" : "13px",
        },
      },
      axisBorder: {
        show: !!xAxisBorderColor,
        color: xAxisBorderColor,
      },
    },
    yaxis: {
      min: 0,
      max: dynamicMax,
      labels: {
        style: {
          fontSize: showVerticaldata ? "10px" : "12px",
          fontWeight: 700,
          fontFamily: "Calibri",
          color: "#000",
        },
      },
      axisBorder: { show: true, color: "#000" },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          plotOptions: {
            bar: {
              columnWidth: "70%",
            },
          },
          chart: {
            height: 400,
          },
        },
      },
      {
        breakpoint: 768,
        options: {
          plotOptions: {
            bar: {
              columnWidth: "80%",
            },
          },
          chart: {
            height: 360,
          },
          xaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
    ],
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
  };

  const formattedSeries = data.map((series, index) => ({
    ...series,
    data: (series.data || []).map((val) => {
      if (val === null || val === undefined) return val;
      if (val > 0 && val < minVisualVal) {
        return minVisualVal;
      }
      return val;
    }),
    name: barNames[index],
  }));

  return (
    <div className="min-w-[340px] [&_.apexcharts-svg]:overflow-visible">
      <ReactApexChart
        key={`${showVerticaldata}_${categories.join("_")}_${realMaxVal}`}
        options={options}
        series={formattedSeries}
        type="bar"
        height={height}
      />
    </div>
  );
};

export default MultipleBarGraph;
